'use client';

import { useState } from 'react';

import { cn } from 'cn';
import Image from 'next/image';
import { useTranslations } from 'use-intl';

import { Button, type Messages } from '@/shared-ui';
import { deduplicateOptions } from '@/shared-utilities';

type ChatMessage = Messages[number];
type Option = NonNullable<ChatMessage['options']>[number];

interface Props {
  message: ChatMessage;
  onSubmit: (content: string) => void | Promise<void>;
  isSubmitting: boolean;
  isConversationOver: boolean;
  /** Show the in-stage Send button. Pass true to render a Send button below the grid. */
  showSendButton: boolean;
  /** When true, tiles are display-only (no click handling, no visual selection changes driven by the stage). */
  readonly?: boolean;
  /** Controlled selection (comma-joined values). When provided with onChange, stage uses these instead of local state. */
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
}

function getGridClassName(count: number): string {
  if (count <= 1) return 'flex justify-center';
  if (count === 2) return 'grid grid-cols-2 gap-4';
  if (count === 3) return 'grid grid-cols-3 gap-4';
  return 'grid grid-cols-4 gap-4';
}

function getTileSizeClassName(count: number): string {
  if (count <= 1) return 'w-full max-w-169.5';
  return 'w-full';
}

function getImageSizes(count: number): string {
  if (count <= 1) return '(max-width: 1024px) 80vw, 678px';
  if (count === 2) return '(max-width: 1024px) 45vw, 472px';
  if (count === 3) return '(max-width: 1024px) 30vw, 309px';
  return '(max-width: 1024px) 22vw, 228px';
}

function toggleSelection(
  current: string,
  clicked: string,
  maxSelections: number | undefined
): string {
  const values = current ? current.split(',').filter(Boolean) : [];
  if (values.includes(clicked)) {
    return values.filter((v) => v !== clicked).join(',');
  }
  if (maxSelections && values.length >= maxSelections) {
    // Replace the oldest selection — matches useImageChoice semantics
    return [...values.slice(0, -1), clicked].join(',');
  }
  return [...values, clicked].join(',');
}

export default function ProjectUsabilityImageChoiceStage({
  message,
  onSubmit,
  isSubmitting,
  isConversationOver,
  showSendButton,
  readonly = false,
  value: controlledValue,
  onChange,
  className,
}: Props) {
  const t = useTranslations('Usability');
  const [localValue, setLocalValue] = useState('');
  const isControlled = controlledValue !== undefined && onChange !== undefined;
  const value = isControlled ? controlledValue : localValue;
  const setValue = (next: string) => {
    if (isControlled) {
      onChange(next);
    } else {
      setLocalValue(next);
    }
  };

  const options: Option[] = deduplicateOptions(message.options ?? []);
  const count = options.length;
  const maxSelections = message.maxSelections ?? undefined;
  const selectedValues = value ? value.split(',').filter(Boolean) : [];
  const isSelected = (v: string) => selectedValues.includes(v);
  const tilesDisabled = isSubmitting || isConversationOver || readonly;

  const handlePick = (optionValue: string) => {
    if (tilesDisabled) return;
    setValue(toggleSelection(value, optionValue, maxSelections));
  };

  const handleSend = () => {
    if (!selectedValues.length || isSubmitting || isConversationOver) return;
    void onSubmit(selectedValues.join(','));
  };

  return (
    <div
      data-test='usability-image-choice-stage'
      className={cn(
        'flex size-full flex-col items-center justify-center overflow-hidden bg-gray-100 px-6 py-8',
        className
      )}
    >
      <div className='flex w-full max-w-240 flex-col items-stretch gap-8'>
        <div
          data-test='usability-image-choice-title'
          className='text-center [font-family:var(--brand-font)] text-[28px]/10.5 font-semibold text-gray-950'
        >
          {message.text}
        </div>

        <div
          data-test='usability-image-choice-grid'
          className={getGridClassName(count)}
        >
          {options.map((option) => {
            const selected = isSelected(option.value);
            const imageUrl = option.assets?.[0]?.url;
            return (
              <Button
                key={option.value}
                type='button'
                variant='ghost'
                onClick={() => handlePick(option.value)}
                disabled={tilesDisabled}
                data-test='usability-image-choice-option'
                data-selected={selected}
                aria-label={t('chooseOptionAria', { label: option.label })}
                aria-pressed={selected}
                className={cn(
                  'flex h-auto flex-col gap-4 border-0 bg-transparent p-0 text-inherit hover:enabled:bg-transparent',
                  getTileSizeClassName(count)
                )}
              >
                <div
                  data-selected={selected}
                  className='relative aspect-5/4 w-full overflow-hidden rounded-2xl border data-[selected=false]:border-gray-300 data-[selected=true]:border-gray-700 data-[selected=true]:bg-white'
                >
                  {imageUrl ? (
                    <Image
                      src={imageUrl}
                      alt={option.label}
                      fill
                      sizes={getImageSizes(count)}
                      data-test='usability-image-choice-image'
                      className='object-contain'

                      unoptimized
                    />
                  ) : (
                    <div className='flex size-full items-center justify-center bg-gray-100 text-sm text-gray-600'>
                      {t('noImage')}
                    </div>
                  )}
                </div>
                <div
                  data-selected={selected}
                  className='w-full truncate text-center text-sm/5 data-[selected=false]:font-normal data-[selected=false]:text-gray-600 data-[selected=true]:font-semibold data-[selected=true]:text-gray-950'
                >
                  {option.label}
                </div>
              </Button>
            );
          })}
        </div>

        {showSendButton ? (
          <div className='flex items-center justify-center'>
            <Button
              type='button'
              onClick={handleSend}
              disabled={
                !selectedValues.length || isSubmitting || isConversationOver
              }
              aria-busy={isSubmitting}
              data-test='usability-image-choice-send'
              aria-label={t('sendAnswerAria')}
              className='min-w-40'
            >
              {t('send')}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
